import { LegalLinksAndVersion } from '@Components/Legal/LegalLinksAndVersion';
import { useAppState, useProfileName } from '@Contexts/appContext';
import { useNavigation } from '@Contexts/navigation';
import { useIsPortalSession, useSession } from '@Contexts/session';
import { useIsSignalRConnected } from '@Contexts/signal';
import { useMenuFiltering } from '@Hooks/Navigation';
import { EnumAccessState, EnumPartnershipConsent } from '@Model';
import { Sidebar } from '@synqo/frontend-component-library';
import { IconLogout, IconSettings, IconStatusChange, IconUser } from '@tabler/icons-react';
import { type ComponentProps, useMemo } from 'react';
import { useNavigate } from 'react-router';

function getSidebarFooterLinks(
    profileName: string,
    navigate: (route: string) => void,
    showSwitchMandant: boolean,
    showSettings: boolean,
    mandantDisplay: string,
): ComponentProps<typeof Sidebar>['footer'] {
    const switchMandantButton = {
        label: `${mandantDisplay} (wechseln)`,
        icon: IconStatusChange,
        action: () => navigate('/login'),
    };

    const settingsButton = { label: 'Einstellungen', icon: IconSettings, link: '/settings' };

    return [
        {
            label: profileName,
            icon: IconUser,
            link: '/profile/me',
        },
        ...(showSettings ? [settingsButton] : []),
        ...(showSwitchMandant ? [switchMandantButton] : []),
        { label: 'Abmelden', icon: IconLogout, action: () => navigate('/signout') },
    ];
}

export function SynqoSidebar() {
    const {
        current: { subpath, partnershipId },
        partnerships,
    } = useSession();
    const {
        profile: { permissions },
    } = useAppState();

    const profileName = useProfileName();
    const isSignalRConnected = useIsSignalRConnected();
    const navigate = useNavigate();

    const { navigationBar } = useNavigation();
    const isPortalSession = useIsPortalSession();

    const elements = useMenuFiltering(navigationBar, permissions);

    const showSwitchMandant = useMemo(
        () =>
            partnerships.filter(
                (p) =>
                    p.access_state === EnumAccessState.Unlocked &&
                    p.partnership_consent === EnumPartnershipConsent.Accepted,
            ).length > 1,
        [partnerships],
    );

    const currentPartnership = useMemo(
        () => partnerships.find((p) => p.partnership_id === partnershipId),
        [partnerships, partnershipId],
    );

    const currentMandantDisplay = currentPartnership?.mandant_display ?? 'Mandant';

    const footer = useMemo(
        () => getSidebarFooterLinks(profileName, navigate, showSwitchMandant, !isPortalSession, currentMandantDisplay),
        [profileName, showSwitchMandant, isPortalSession, currentMandantDisplay, navigate],
    );

    return (
        <Sidebar
            main={elements}
            footer={footer}
            linkPrefix={`/${subpath}`}
            home={`/${subpath}`}
            legalLinks={<LegalLinksAndVersion isBackendConnected={isSignalRConnected} />}
        />
    );
}
